import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import {
  TFunction,
  canDeleteField,
  formatNumber,
  useAddFieldPracticeParticipations,
  useDeleteFieldPracticeParticipations,
  useGetAllCarbonProgramOutcomes,
  useGetFarmReportingYear,
  useGetFarms,
  useTranslation,
} from "utils";

import { FERTILIZER_BUNDLE_SLUGS, TILLAGE_SEEDING_BUNDLE_SLUGS } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  CarbonProgram,
  FieldDetails as FieldDetailsType,
  FieldPracticeResponse,
  PRACTICE_BUNDLE_SLUG,
  PRACTICE_SLUG,
  PracticeBundle,
  VIEW_TYPE,
} from "models";

import { ContextMenu, Flex, Icon, ItemProps } from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

type FCPOMenuProps = {
  availablePracticeBundles: FieldPracticeResponse["availablePracticeBundles"];
  fieldId: string;
  isEnrolled: boolean;
  setShowFieldEditDrawer: Dispatch<SetStateAction<boolean>>;
  setShowDeleteFieldModal: Dispatch<SetStateAction<boolean>>;
  carbonProgram?: CarbonProgram | null;
  isPracticeEnrollmentDisabled?: boolean;
  fieldDetails?: FieldDetailsType;
  setPracticeBundles?: Dispatch<SetStateAction<PracticeBundle[] | undefined>>;
  placement?: "right" | "left" | "bottom-left" | "bottom-right";
  viewType?: VIEW_TYPE;
};

type ContextMenuHeaderProps = {
  viewType: VIEW_TYPE;
  fieldDetails?: FieldDetailsType;
  isEnrolled: boolean;
  t: TFunction;
};

const ContextMenuHeader = ({ viewType, fieldDetails, isEnrolled, t }: ContextMenuHeaderProps) => {
  if (viewType === VIEW_TYPE.ALL_FIELDS || !fieldDetails) return <></>;

  const { name, size, isInRedzone } = fieldDetails;

  return (
    <Flex vertical={true} className="field-timeline-context-menu-header" justify="center" onClick={(e) => e.stopPropagation()}>
      <span className="field-timeline-context-menu-field-name" data-testid="field-timeline-context-menu-field-name">
        {name}
      </span>
      <Flex className="field-timeline-context-menu-additional-data" justify="center">
        {isEnrolled ? <Icon className="enrollment-icon" dataTestId="field-timeline-context-menu-enrollment-icon" iconName="klim/fcpo-enrollment" /> : <></>}
        <span className="field-timeline-context-menu-field-size" data-testid="field-timeline-context-menu-field-size">
          {`${formatNumber({ number: size || 0, decimalPlaces: 2 })} ha`}
        </span>
        {isInRedzone && (
          <span className="field-timeline-context-menu-field-red-zone" data-testid="field-timeline-context-menu-red-zone">
            &nbsp;&#183; {t("fieldTimeline:header.isInRedzone")}
          </span>
        )}
      </Flex>
    </Flex>
  );
};

export const FCPOMenu = ({
  availablePracticeBundles,
  fieldId,
  isEnrolled,
  setShowFieldEditDrawer,
  setShowDeleteFieldModal,
  carbonProgram,
  isPracticeEnrollmentDisabled,
  fieldDetails,
  setPracticeBundles,
  placement = "right",
  viewType,
}: FCPOMenuProps) => {
  const { t } = useTranslation();

  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const isBundleActive = useCallback(
    (bundle: string) => (availablePracticeBundles.filter((pb) => pb.slug === bundle).map((pb) => pb.active) || [])?.shift() || false,
    [availablePracticeBundles]
  );
  const [toggleTillage, setToggleTillage] = useState(isBundleActive("tillage"));
  const [toggleFertilizer, setToggleFertilizer] = useState(isBundleActive("fertilizer"));

  const { currentFarm } = useGetFarms();

  const { mutate: addFieldPracticeParticipations } = useAddFieldPracticeParticipations(fieldDetails?.id || "", currentFarm?.id);
  const { mutate: deleteFieldPracticeParticipations } = useDeleteFieldPracticeParticipations(fieldDetails?.id || "", currentFarm?.id);

  const { data: carbonProgramOutcomes } = useGetAllCarbonProgramOutcomes(currentFarm?.id, currentFarm?.carbonProgram);

  const { data: activeFarmReportingYear } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isDataSubmitted: isDataSubmittedForCurrentReportingYear } = useSubmissionStatus(activeFarmReportingYear);

  const bundleHasConfirmedData = (bundleSlug: string) => !!availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.hasConfirmedData;
  const bundleIsDisabled = (bundleSlug: string) => !availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.enrollable;
  const bundleIsDiscontinued = (bundleSlug: string) =>
    Number.isInteger(availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.discontinuedYear);

  const cropBundle = availablePracticeBundles.find((bundle) => bundle.slug === "crops");
  const cropsIsActive = !!cropBundle?.active;
  const fieldEnrollmentCheckboxState =
    isPracticeEnrollmentDisabled || bundleIsDiscontinued("crops") || bundleHasConfirmedData("crops") || isDataSubmittedForCurrentReportingYear
      ? ("disabled" as ItemProps["state"])
      : undefined;

  const tillageBundle = availablePracticeBundles.find((bundle) => bundle.slug === "tillage");
  const tillageIsActive = !!tillageBundle?.active;
  const tillageBundleCheckboxState =
    isPracticeEnrollmentDisabled ||
    bundleIsDisabled("tillage") ||
    bundleIsDiscontinued("tillage") ||
    !cropsIsActive ||
    bundleHasConfirmedData("tillage") ||
    isDataSubmittedForCurrentReportingYear
      ? ("disabled" as ItemProps["state"])
      : undefined;

  const fertilizerBundle = availablePracticeBundles.find((bundle) => bundle.slug === "fertilizer");
  const fertilizerIsActive = !!fertilizerBundle?.active;
  const fertilizerBundleCheckboxState =
    isPracticeEnrollmentDisabled ||
    bundleIsDisabled("fertilizer") ||
    bundleIsDiscontinued("fertilizer") ||
    !cropsIsActive ||
    bundleHasConfirmedData("fertilizer") ||
    isDataSubmittedForCurrentReportingYear
      ? ("disabled" as ItemProps["state"])
      : undefined;

  const handleBundleToggle = (bundleSlug: PracticeBundle["slug"], value: boolean) => {
    if (!fieldId) return;

    const practiceSlugMap = {
      crops: ["crops"],
      fertilizer: FERTILIZER_BUNDLE_SLUGS,
      tillage: TILLAGE_SEEDING_BUNDLE_SLUGS,
    };

    const practiceSlugs = bundleSlug === "crops" && !value ? Object.values(PRACTICE_SLUG) : practiceSlugMap[bundleSlug];

    if (value) {
      addFieldPracticeParticipations({ fieldId, practiceSlugs });
    } else {
      deleteFieldPracticeParticipations({ fieldId, practiceSlugs });
    }

    if (viewType === VIEW_TYPE.SINGLE_FIELD) {
      setPracticeBundles?.((prevBundles) => prevBundles?.map((bundle) => (bundle.slug === bundleSlug ? { ...bundle, active: !bundle.active } : bundle)) ?? []);
    }
  };

  const contextMenuOnToggle = (bundleSlug: PracticeBundle["slug"], value: boolean) => {
    if (bundleSlug === "tillage") setToggleTillage(!toggleTillage);
    if (bundleSlug === "fertilizer") setToggleFertilizer(!toggleFertilizer);
    handleBundleToggle(bundleSlug, value);
  };

  const isFieldDeletable = canDeleteField({
    carbonProgramResult: carbonProgramOutcomes?.[fieldId],
    carbonProgram,
  });

  useEffect(() => {
    setToggleTillage(isBundleActive("tillage"));
    setToggleFertilizer(isBundleActive("fertilizer"));
  }, [availablePracticeBundles, isBundleActive]);

  return (
    <>
      <ContextMenu
        className="field-timeline"
        dataTestId="field-timeline-context-menu"
        items={[
          ...(viewType === VIEW_TYPE.SINGLE_FIELD
            ? [
                {
                  iconName: "",
                  label: <ContextMenuHeader viewType={viewType} fieldDetails={fieldDetails} isEnrolled={isEnrolled} t={t} />,
                  isFirstItem: true,
                },
              ]
            : []),
          {
            iconName: "tabler/edit",
            label: t("farm:contextMenu.editFieldDetails"),
            onClick: () => setShowFieldEditDrawer(true),
          },

          ...(carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory
            ? []
            : [
                {
                  hasCheckbox: true,
                  iconName: "tabler/certificate",
                  isChecked: isEnrolled,
                  label: t("farm:contextMenu.addFieldToPayout"),
                  state: fieldEnrollmentCheckboxState,
                  onCheck: (value: boolean) => contextMenuOnToggle(PRACTICE_BUNDLE_SLUG.CROPS, value),
                  style: { borderRight: `${bundleIsDiscontinued("crops") ? "0.5rem solid #ff845e" : ""}` },
                },
                {
                  hasToggle: true,
                  iconName: "klim/tillage",
                  isToggled: isEnrolled && tillageIsActive,
                  label: t("farm:contextMenu.addTillageAndSedding"),
                  state: tillageBundleCheckboxState,
                  onClick: () => contextMenuOnToggle(PRACTICE_BUNDLE_SLUG.TILLAGE, !tillageIsActive),
                  onToggle: () => contextMenuOnToggle(PRACTICE_BUNDLE_SLUG.TILLAGE, !tillageIsActive),
                  style: { borderRight: `${bundleIsDiscontinued("tillage") ? "0.5rem solid #ff845e" : ""}` },
                },
                {
                  hasToggle: true,
                  iconName: "klim/fertilizer",
                  isToggled: isEnrolled && fertilizerIsActive,
                  label: t("farm:contextMenu.addFertilizer"),
                  state: fertilizerBundleCheckboxState,
                  onClick: () => contextMenuOnToggle(PRACTICE_BUNDLE_SLUG.FERTILIZER, !fertilizerIsActive),
                  onToggle: () => contextMenuOnToggle(PRACTICE_BUNDLE_SLUG.FERTILIZER, !fertilizerIsActive),
                  style: { borderRight: `${bundleIsDiscontinued("fertilizer") ? "0.5rem solid #ff845e" : ""}` },
                },
              ]),
          {
            iconName: "tabler/trash",
            label: t("farm:contextMenu.deleteField"),
            onClick: () => setShowDeleteFieldModal?.(true),
            state: isFieldDeletable ? ("warning" as ItemProps["state"]) : "disabled",
          },
        ]}
        placement={placement}
        iconSize="m-light"
        visible={showContextMenu}
        onClose={() => setShowContextMenu(false)}
      >
        <div
          className="context-menu-click-target"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowContextMenu(true);
          }}
        >
          <Icon
            className="header-dots-vertical-icon"
            iconName="tabler/dots-vertical"
            strokeWidth={2}
            dataTestId="dots-vertical-icon"
            onClick={() => setShowContextMenu(!showContextMenu)}
          />
        </div>
      </ContextMenu>
    </>
  );
};
